import { useEffect, useState, Component, lazy, Suspense, type ReactNode } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/auth'
import { useCashflowStore } from './stores/cashflow'
import { useUIStore } from './stores/ui'
import { useHistoryStore } from './stores/history'
import { useToastStore } from './stores/toast'
import Layout from './components/Layout'
import ToastContainer from './components/ToastContainer'

// 認証前後の入口は静的import（初回表示の遅延を防ぐ）
import LoginPage from './pages/LoginPage'
import SetupPage from './pages/SetupPage'
import SsoLoginPage from './pages/SsoLoginPage'
import InputPage from './pages/InputPage'

// 動的import（ページ訪問時に遅延ロード）。
// 特に DashboardPage は recharts (gzip 105KB) を含むため初回ロードが軽くなる。
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const SpreadsheetPage = lazy(() => import('./pages/SpreadsheetPage'))
const MastersPage = lazy(() => import('./pages/MastersPage'))
const BanksPage = lazy(() => import('./pages/BanksPage'))
const ReschedulePage = lazy(() => import('./pages/ReschedulePage'))
const RescheduleDetailPage = lazy(() => import('./pages/RescheduleDetailPage'))
const RecurringPage = lazy(() => import('./pages/RecurringPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const HistoryPage = lazy(() => import('./pages/HistoryPage'))
const TrashPage = lazy(() => import('./pages/TrashPage'))
const BackupPage = lazy(() => import('./pages/BackupPage'))
const PermissionsPage = lazy(() => import('./pages/PermissionsPage'))
const IpAllowlistPage = lazy(() => import('./pages/IpAllowlistPage'))
const LoginHistoryPage = lazy(() => import('./pages/LoginHistoryPage'))
const AlertSettingsPage = lazy(() => import('./pages/AlertSettingsPage'))
const AuditExportPage = lazy(() => import('./pages/AuditExportPage'))
const MfaSetupPage = lazy(() => import('./pages/MfaSetupPage'))

const PageFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-blue-100 border-t-blue-600" />
  </div>
)

// エラーバウンダリ
class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    if (this.state.error) {
      return (
        <div className="p-8 text-center">
          <h2 className="text-lg font-bold text-red-600 mb-2">エラーが発生しました</h2>
          <p className="text-sm text-gray-600 mb-4">{this.state.error.message}</p>
          <button onClick={() => { this.setState({ error: null }); window.location.reload() }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">
            リロード
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

export default function App() {
  const { status, checkAuth, user, logout, isSetupSession } = useAuthStore()
  const loadAll = useCashflowStore((s) => s.loadAll)

  useEffect(() => { checkAuth() }, [checkAuth])
  // 収支登録専用ユーザーは資金繰り表等を読まない（403エラーが出るため）
  // setup session 中も業務 API が 403 を返すので loadAll を呼ばない
  useEffect(() => {
    if (status === 'authenticated' && !user?.inputOnly && !isSetupSession) loadAll()
  }, [status, user?.inputOnly, isSetupSession, loadAll])

  // 2026-05-14 追加: MFA未設定 admin が業務画面に直接アクセス（ブラウザ再読込・URL貼付け等）
  // した場合は、業務 API が 403 を連発するため、必ず /mfa/setup に強制誘導する。
  // /mfa/setup 自体に居る間は遷移しない。
  useEffect(() => {
    if (status !== 'authenticated' || !isSetupSession) return
    if (typeof window === 'undefined') return
    if (window.location.pathname.startsWith('/mfa/setup')) return
    // 履歴を汚さないように replace。完全リロードは不要（React Router で十分）
    window.history.replaceState(null, '', '/mfa/setup')
    // 次のレンダリングサイクルでルーティングが追従するように、状態通知用にイベント発火
    window.dispatchEvent(new PopStateEvent('popstate'))
  }, [status, isSetupSession])

  // ZER-325: DB の app_settings.session.idle_timeout_minutes を読み取り、タイムアウト時間を可変化
  // 取得失敗時は安全側で 60 分にフォールバック
  const [idleTimeoutMin, setIdleTimeoutMin] = useState(60)
  useEffect(() => {
    if (status !== 'authenticated') return
    let cancelled = false
    fetch('/api/settings', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return
        const raw = data?.settings?.['session.idle_timeout_minutes']
        const v = parseInt(raw || '60')
        // 5分以上 480分（8時間）以下に制約。これより短い/長い設定は誤入力扱いでデフォルト60分にする
        if (Number.isFinite(v) && v >= 5 && v <= 480) setIdleTimeoutMin(v)
      })
      .catch(() => { /* ignore */ })
    return () => { cancelled = true }
  }, [status])

  // セッションタイムアウト警告: (idle_timeout - 5分) で警告、idle_timeout で自動ログアウト
  // 操作（click/keydown）があれば都度タイマーリセット
  useEffect(() => {
    if (status !== 'authenticated') return
    let warnTimer = 0
    let logoutTimer = 0
    let warned = false

    const timeoutMs = idleTimeoutMin * 60 * 1000
    // タイムアウトが10分以下なら警告は半分の地点、それ以外は 5 分前に出す
    const warnMs = timeoutMs > 10 * 60 * 1000 ? timeoutMs - 5 * 60 * 1000 : Math.floor(timeoutMs / 2)
    const warnRemainMin = Math.max(1, Math.ceil((timeoutMs - warnMs) / 60000))

    const resetTimers = () => {
      window.clearTimeout(warnTimer)
      window.clearTimeout(logoutTimer)
      warned = false
      warnTimer = window.setTimeout(() => {
        warned = true
        useToastStore.getState().error(
          `あと${warnRemainMin}分で自動ログアウトします。画面を操作すると延長されます`,
          15000
        )
      }, warnMs)
      logoutTimer = window.setTimeout(() => {
        useToastStore.getState().info('セッションがタイムアウトしました。再ログインしてください')
        logout()
      }, timeoutMs)
    }

    const onActivity = () => {
      // 警告後の延長は、意図的な操作のみ受け付ける（1度だけリセット）
      if (warned) return
      resetTimers()
    }

    resetTimers()
    window.addEventListener('click', onActivity)
    window.addEventListener('keydown', onActivity)

    return () => {
      window.clearTimeout(warnTimer)
      window.clearTimeout(logoutTimer)
      window.removeEventListener('click', onActivity)
      window.removeEventListener('keydown', onActivity)
    }
  }, [status, logout, idleTimeoutMin])

  // 未保存変更の警告: 保存中のmutationがある時にページ離脱しようとすると確認
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (useUIStore.getState().pendingMutations > 0) {
        e.preventDefault()
        e.returnValue = '保存中の変更があります。ページを離れると失われる可能性があります。'
        return e.returnValue
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  // グローバルキーボードショートカット: Cmd/Ctrl+Z で戻る、Cmd/Ctrl+Shift+Z で進む
  useEffect(() => {
    const handler = async (e: KeyboardEvent) => {
      // input/textareaにフォーカス中はスキップ
      const target = e.target as HTMLElement
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      const meta = e.metaKey || e.ctrlKey
      if (!meta) return
      if (e.key === 'z' || e.key === 'Z') {
        e.preventDefault()
        const toast = useToastStore.getState()
        const history = useHistoryStore.getState()
        try {
          if (e.shiftKey) {
            const action = await history.redo()
            if (action) toast.success(`やり直しました: ${action.label}`)
            else toast.info('進む操作がありません')
          } else {
            const action = await history.undo()
            if (action) toast.success(`元に戻しました: ${action.label}`)
            else toast.info('戻る操作がありません')
          }
        } catch (err) {
          toast.error(`操作失敗: ${err instanceof Error ? err.message : ''}`)
        }
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-blue-100 border-t-blue-600" />
      </div>
    )
  }

  // /sso?token=xxx はアドバイザーポータルからのSSOログイン処理を最優先で表示。
  // 認証状態に関わらずトークンを処理する必要があるため、ここで早期分岐。
  if (typeof window !== 'undefined' && window.location.pathname === '/sso') {
    return (<><SsoLoginPage /><ToastContainer /></>)
  }
  if (status === 'setup') return (<><SetupPage /><ToastContainer /></>)
  if (status === 'login') return (<><LoginPage /><ToastContainer /></>)

  // allowed_pages が 1件だけのユーザーはそのページに限定（UIもシンプルな独立レイアウト）
  if (user?.role !== 'admin' && user?.allowedPages && user.allowedPages.length === 1 && user.allowedPages[0] === 'input') {
    return (
      <ErrorBoundary>
        <ToastContainer />
        <Routes>
          <Route path="/input" element={<InputPage />} />
          <Route path="*" element={<Navigate to="/input" replace />} />
        </Routes>
      </ErrorBoundary>
    )
  }

  // シークレット権限あり = シークレットページ閲覧可。トグル概念は廃止しシンプルに。
  const isAdmin = user?.role === 'admin'
  const canSeeSecret = isAdmin || !!user?.canViewSecret

  // 初期リダイレクト先: 表示可能な最初のページ。シークレット権限なしで初期遷移先がシークレットページなら input にフォールバック。
  const firstAllowed = (() => {
    const SECRET_PATHS = new Set(['/dashboard', '/table', '/banks', '/history', '/reschedule', '/trash', '/backup'])
    if (user?.role === 'admin' || !user?.allowedPages) {
      return canSeeSecret ? '/dashboard' : '/input'
    }
    const first = user.allowedPages[0]
    const pathMap: Record<string, string> = {
      dashboard: '/dashboard', table: '/table', input: '/input',
      banks: '/banks', masters: '/masters',
      debt_reschedule: '/reschedule', reschedule: '/reschedule',
      recurring: '/recurring', history: '/history', trash: '/trash',
      backup: '/backup', settings: '/settings',
    }
    const candidate = pathMap[first] || '/dashboard'
    if (SECRET_PATHS.has(candidate) && !canSeeSecret) {
      // シークレットページが先頭にあるが見れないので、非シークレットの最初の許可ページを探す
      for (const p of user.allowedPages) {
        const path = pathMap[p]
        if (path && !SECRET_PATHS.has(path)) return path
      }
      return '/input'
    }
    return candidate
  })()

  // シークレットページを直接 URL アクセスから保護するラッパー。
  // canSeeSecret が false（=admin でなく、かつ canViewSecret が無い or secretMode OFF）なら /input へ。
  const secretGuard = (el: React.ReactNode) => canSeeSecret ? el : <Navigate to="/input" replace />

  return (
    <ErrorBoundary>
      <ToastContainer />
      <Layout>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Navigate to={firstAllowed} replace />} />
            <Route path="/dashboard" element={secretGuard(<DashboardPage />)} />
            <Route path="/input" element={<InputPage />} />
            <Route path="/table" element={secretGuard(<SpreadsheetPage />)} />
            <Route path="/masters" element={<MastersPage />} />
            <Route path="/reschedule" element={secretGuard(<ReschedulePage />)} />
            <Route path="/reschedules/:id" element={secretGuard(<RescheduleDetailPage />)} />
            <Route path="/recurring" element={<RecurringPage />} />
            <Route path="/history" element={secretGuard(<HistoryPage />)} />
            <Route path="/trash" element={secretGuard(<TrashPage />)} />
            <Route path="/backup" element={secretGuard(<BackupPage />)} />
            <Route path="/permissions" element={<PermissionsPage />} />
            <Route path="/ip-allowlist" element={<IpAllowlistPage />} />
            <Route path="/login-history" element={<LoginHistoryPage />} />
            <Route path="/alerts" element={<AlertSettingsPage />} />
            <Route path="/audit-export" element={<AuditExportPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/mfa/setup" element={<MfaSetupPage />} />
            {/* 銀行口座は独立ページ（重要マスタ・シークレット）*/}
            <Route path="/banks" element={secretGuard(<BanksPage />)} />
            {/* 旧ルートとの互換 */}
            <Route path="/vendors" element={<Navigate to="/masters" replace />} />
            <Route path="/debt" element={<Navigate to="/reschedule" replace />} />
            <Route path="/debt-reschedule" element={<Navigate to="/reschedule" replace />} />
            <Route path="/summary" element={<Navigate to="/table" replace />} />
          </Routes>
        </Suspense>
      </Layout>
    </ErrorBoundary>
  )
}
